import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Input,
  TextArea,
  LoadingSpinner,
  PageHeader,
  ErrorState,
  MimeRequestCard,
} from '../components';
import { eventService, applicationService } from '../services';
import mimeService from '../services/mimeService';
import { useAuth, useUser } from '../context';

/**
 * Party Supplies section — guests can claim items they'll bring
 */
function PartySuppliesSection({ supplies, onClaim }) {
  const { profile } = useUser();
  const [claimingId, setClaimingId] = useState(null);

  const handleClaim = async (supplyId, claim) => {
    setClaimingId(supplyId);
    await onClaim(supplyId, claim);
    setClaimingId(null);
  };

  return (
    <div className="border-2 border-black p-6 mb-8">
      <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black mb-4">
        PARTY SUPPLIES ({supplies.filter((s) => s.isClaimed).length}/{supplies.length} CLAIMED)
      </h3>
      <div className="space-y-3">
        {supplies.map((supply, index) => (
          <div key={supply.id} className="flex items-center gap-3 p-2 border border-gray-200">
            {/* Thumbnail */}
            {supply.imageUrl && (
              <div className="w-10 h-10 flex-shrink-0 border border-black overflow-hidden">
                <img src={supply.imageUrl} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            {/* Item text */}
            <div className="flex-1 min-w-0">
              <p className="font-mono text-sm text-black truncate">
                {index + 1}. {supply.text}
              </p>
              {supply.isClaimed && supply.claimedByDisplayName && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="w-4 h-4 flex-shrink-0 border border-black overflow-hidden bg-gray-100">
                    {supply.claimedByAvatarUrl ? (
                      <img src={supply.claimedByAvatarUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="flex items-center justify-center w-full h-full font-mono text-[8px] font-bold text-gray-400">
                        {supply.claimedByDisplayName?.charAt(0)?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-xs text-gray-500 truncate">
                    {supply.claimedByDisplayName}
                  </p>
                </div>
              )}
            </div>
            {/* Action */}
            {!supply.isClaimed ? (
              <button
                type="button"
                onClick={() => handleClaim(supply.id, true)}
                disabled={claimingId === supply.id}
                className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border-2 border-black bg-black text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                {claimingId === supply.id ? '...' : "I'LL BRING IT"}
              </button>
            ) : supply.claimedByUserId === profile?.userId ? (
              <button
                type="button"
                onClick={() => handleClaim(supply.id, false)}
                disabled={claimingId === supply.id}
                className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border-2 border-gray-400 text-gray-600 hover:border-red-500 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                {claimingId === supply.id ? '...' : 'UNCLAIM'}
              </button>
            ) : (
              <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider text-gray-400 flex-shrink-0">
                CLAIMED
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Inner form component that uses P$ wallet balance
 */
function ApplyForm({ event, onSuccess }) {
  const navigate = useNavigate();
  const { profile, walletBalance, refreshWallet } = useUser();

  const [pledgeAmount, setPledgeAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [useGoldenTicket, setUseGoldenTicket] = useState(false);
  const [agreedToRules, setAgreedToRules] = useState(false);

  // Check if user can use a golden ticket
  const hasGoldenTickets = profile?.subscriptionTier === 'pro' && profile?.goldenTicketsRemaining > 0;
  const isCuratedEvent = event?.mode === 'curated';

  // Set default pledge amount to ticket price
  useEffect(() => {
    if (event && !pledgeAmount) {
      if (event.ticketPriceCents === 0) {
        setPledgeAmount('0.00');
      } else if (event.ticketPriceCents) {
        setPledgeAmount((event.ticketPriceCents / 100).toFixed(2));
      }
    }
  }, [event, pledgeAmount]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setError(null);

    try {
      // Validate pledge amount
      const pledgeCents = Math.round(parseFloat(pledgeAmount) * 100);
      if (isNaN(pledgeCents) || pledgeCents < (event.ticketPriceCents || 0)) {
        throw new Error(
          `Pledge must be at least P$${((event.ticketPriceCents || 0) / 100).toFixed(2)}`
        );
      }

      // Check wallet balance
      if ((walletBalance || 0) < pledgeCents) {
        throw new Error(
          `Insufficient P$ balance. You need P$${(pledgeCents / 100).toFixed(2)} but only have P$${((walletBalance || 0) / 100).toFixed(2)}. Top up your wallet first.`
        );
      }

      // Check house rules agreement
      const hasRules = event.houseRules && event.houseRules.length > 0;
      if (hasRules && !agreedToRules) {
        throw new Error('You must agree to the house rules before applying');
      }

      // Create application with P$ payment
      const applicationResult = await applicationService.createApplication(event.id, {
        message: message.trim(),
        pledgeAmountCents: pledgeCents,
        useGoldenTicket: useGoldenTicket && isCuratedEvent,
        agreedToHouseRules: agreedToRules,
      });

      if (!applicationResult.success) {
        const msg = typeof applicationResult.error === 'string'
          ? applicationResult.error
          : applicationResult.error?.message || 'Failed to submit application';
        throw new Error(msg);
      }

      // Refresh wallet balance after successful application
      await refreshWallet();

      // Success!
      onSuccess(applicationResult.data);
    } catch (err) {
      console.error('Application error:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const ticketPriceCoins = (event?.ticketPriceCents || 0) / 100;
  const walletBalanceCoins = (walletBalance || 0) / 100;
  const pledgeCents = Math.round(parseFloat(pledgeAmount || '0') * 100);
  const hasInsufficientBalance = (walletBalance || 0) < pledgeCents;
  const hasRules = event?.houseRules && event.houseRules.length > 0;
  const rulesBlocking = hasRules && !agreedToRules;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-4 border-2 border-red-600 bg-red-50">
          <p className="font-mono text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Wallet Balance Display */}
      <div className="p-4 border-2 border-black bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
              YOUR P$ BALANCE
            </p>
            <p className="font-mono text-2xl font-bold text-black">
              P${walletBalanceCoins.toFixed(2)}
            </p>
          </div>
          {hasInsufficientBalance && pledgeAmount && (
            <button
              type="button"
              onClick={() => navigate('/wallet')}
              className="px-4 py-2 border-2 border-black bg-black text-white font-mono text-xs font-semibold uppercase tracking-wider hover:bg-gray-800 transition-colors"
            >
              TOP UP
            </button>
          )}
        </div>
        {hasInsufficientBalance && pledgeAmount && (
          <p className="mt-2 font-mono text-xs text-red-600">
            Insufficient balance. You need P${(pledgeCents / 100).toFixed(2)} to apply.
          </p>
        )}
      </div>

      {/* House Rules */}
      {hasRules && (
        <div className="border-2 border-black p-4">
          <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black mb-3">
            HOUSE RULES
          </h3>
          <div className="space-y-2 mb-4">
            {event.houseRules.map((rule, index) => (
              <div key={rule.id} className="flex items-start gap-2">
                <span className="font-mono text-xs text-gray-400 w-6 text-right flex-shrink-0 mt-0.5">
                  {index + 1}.
                </span>
                <p className="font-mono text-sm text-gray-700">{rule.text}</p>
              </div>
            ))}
          </div>
          <div className="border-t-2 border-black pt-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToRules}
                onChange={(e) => setAgreedToRules(e.target.checked)}
                disabled={isSubmitting}
                className="w-5 h-5 border-2 border-black accent-black"
              />
              <span className="font-mono text-sm font-semibold uppercase tracking-wider text-black">
                I AGREE TO THE HOUSE RULES
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Pledge Amount */}
      {event?.ticketPriceCents === 0 ? (
        <div className="p-4 border-2 border-black bg-gray-50">
          <p className="font-mono text-sm font-semibold uppercase tracking-wider text-black">
            FREE EVENT — NO PLEDGE REQUIRED
          </p>
        </div>
      ) : (
        <div>
          <label className="block font-mono text-sm font-semibold uppercase tracking-wider mb-2 text-black">
            Pledge Amount (P$) <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 transform -translate-y-1/2 font-mono text-gray-500">
              P$
            </span>
            <input
              type="number"
              value={pledgeAmount}
              onChange={(e) => setPledgeAmount(e.target.value)}
              min={ticketPriceCoins}
              step="0.01"
              disabled={isSubmitting}
              className="
                w-full pl-10 pr-4 py-3
                border-2 border-black
                bg-white text-black
                font-mono text-sm
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2
                disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50
              "
            />
          </div>
          <p className="mt-2 font-mono text-xs text-gray-600">
            Minimum: P${ticketPriceCoins.toFixed(2)} (ticket price)
          </p>
        </div>
      )}

      {/* Optional Message */}
      <TextArea
        label="Message to Host (Optional)"
        placeholder="Tell the host why you'd like to attend..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        maxLength={500}
        disabled={isSubmitting}
      />

      {/* P$ Escrow Info */}
      <div className="p-4 border-2 border-gray-300 bg-gray-50">
        <p className="font-mono text-xs text-gray-600">
          Your P$ will be held in escrow until the event is greenlighted. If your application is declined or the event is cancelled, your P$ will be refunded.
        </p>
      </div>

      {/* Golden Ticket Option */}
      {isCuratedEvent && hasGoldenTickets && (
        <div className="p-4 border-2 border-yellow-400 bg-yellow-50">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="useGoldenTicket"
              checked={useGoldenTicket}
              onChange={(e) => setUseGoldenTicket(e.target.checked)}
              disabled={isSubmitting}
              className="w-5 h-5 mt-0.5 border-2 border-black accent-yellow-500"
            />
            <label htmlFor="useGoldenTicket" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🎟️</span>
                <span className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                  Use Golden Ticket
                </span>
                <span className="px-2 py-0.5 bg-yellow-400 font-mono text-xs font-bold text-black">
                  {profile?.goldenTicketsRemaining} left
                </span>
              </div>
              <p className="font-mono text-xs text-gray-700">
                Skip the queue and get auto-accepted instantly. Pro members get 2 Golden Tickets per month.
              </p>
            </label>
          </div>
        </div>
      )}

      {/* Golden Ticket Upsell for Non-Pro Users */}
      {isCuratedEvent && !hasGoldenTickets && profile?.subscriptionTier !== 'pro' && (
        <div className="p-4 border-2 border-gray-300 bg-gray-50">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">🎟️</span>
            <span className="font-mono text-sm font-bold uppercase tracking-wider text-gray-600">
              Want to Skip the Queue?
            </span>
          </div>
          <p className="font-mono text-xs text-gray-600 mb-2">
            Pro members get 2 Golden Tickets per month for instant acceptance.
          </p>
          <button
            type="button"
            onClick={() => navigate('/pro')}
            className="font-mono text-xs font-bold text-black underline hover:no-underline"
          >
            Upgrade to Pro →
          </button>
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-6">
        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          disabled={isSubmitting || hasInsufficientBalance || rulesBlocking}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner size="sm" />
              PROCESSING...
            </span>
          ) : useGoldenTicket ? (
            <span className="flex items-center justify-center gap-2">
              🎟️ USE GOLDEN TICKET & PLEDGE P${pledgeAmount || '0.00'}
            </span>
          ) : event?.ticketPriceCents === 0 ? (
            'APPLY — FREE'
          ) : (
            `APPLY & PLEDGE P$${pledgeAmount || '0.00'}`
          )}
        </Button>
      </div>

      {/* Cancel link */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="font-mono text-sm text-gray-600 uppercase tracking-wider hover:text-black transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * EventApplyPage - Apply to an Event
 * Design: Event details with application form and P$ wallet payment
 */
export default function EventApplyPage() {
  const navigate = useNavigate();
  const { inviteCode: eventId } = useParams();
  const { isAuthenticated } = useAuth();

  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [submittedApplication, setSubmittedApplication] = useState(null);
  const [existingApplication, setExistingApplication] = useState(null);
  const [mimeRequests, setMimeRequests] = useState([]);
  const [mimeHistory, setMimeHistory] = useState([]);
  const [respondingMimeId, setRespondingMimeId] = useState(null);
  const [mimeErrors, setMimeErrors] = useState({});

  // Fetch event details and check for existing application
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Try to fetch by invite code first, then by ID
        let result = await eventService.getEventByCode(eventId);
        if (!result.success) {
          result = await eventService.getEvent(eventId);
        }

        if (!result.success) {
          throw new Error(result.error || 'Event not found');
        }

        // Check if event is accepting applications (published/pledging status)
        const acceptingStatuses = ['published', 'pledging'];
        if (!acceptingStatuses.includes(result.data.status)) {
          throw new Error('This event is not accepting applications');
        }

        setEvent(result.data);

        // Check if user already has an application for this event
        const myApps = await applicationService.getMyApplications();
        if (myApps.success && myApps.data) {
          const apps = Array.isArray(myApps.data) ? myApps.data : myApps.data.applications || [];
          const existing = apps.find(
            (a) => a.eventId === result.data.id || a.event?.id === result.data.id
          );
          if (existing) {
            setExistingApplication(existing);

            // Fetch all MIME requests for this event (history + pending)
            const mimeResult = await mimeService.getMyRequestsForEvent(result.data.id);
            if (mimeResult.success && mimeResult.data) {
              const allRequests = mimeResult.data;
              setMimeHistory(allRequests.filter((r) => r.status !== 'Pending'));
              setMimeRequests(allRequests.filter((r) => r.status === 'Pending'));
            }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (eventId) {
      fetchData();
    }
  }, [eventId]);

  // Handle successful application
  const handleSuccess = (application) => {
    setSubmittedApplication(application);
    setApplicationSuccess(true);
  };

  // Handle MIME accept/decline
  const handleMimeAccept = async (requestId) => {
    setRespondingMimeId(requestId);
    setMimeErrors((prev) => ({ ...prev, [requestId]: null }));
    const result = await mimeService.respondToRequest(requestId, true);
    if (result.success) {
      setMimeRequests((prev) => {
        const accepted = prev.find((r) => r.id === requestId);
        if (accepted) {
          setMimeHistory((h) => [...h, { ...accepted, status: 'Accepted', committedAmountCents: accepted.additionalCommitmentCents }]);
        }
        return prev.filter((r) => r.id !== requestId);
      });
    } else {
      const msg = result.error?.message || result.error || 'Failed to commit';
      setMimeErrors((prev) => ({ ...prev, [requestId]: msg }));
    }
    setRespondingMimeId(null);
  };

  const handleMimeDecline = async (requestId) => {
    setRespondingMimeId(requestId);
    setMimeErrors((prev) => ({ ...prev, [requestId]: null }));
    const result = await mimeService.respondToRequest(requestId, false);
    if (result.success) {
      setMimeRequests((prev) => {
        const declined = prev.find((r) => r.id === requestId);
        if (declined) {
          setMimeHistory((h) => [...h, { ...declined, status: 'Declined' }]);
        }
        return prev.filter((r) => r.id !== requestId);
      });
    } else {
      const msg = result.error?.message || result.error || 'Failed to decline';
      setMimeErrors((prev) => ({ ...prev, [requestId]: msg }));
    }
    setRespondingMimeId(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Auth check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h1 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
            SIGN IN TO APPLY
          </h1>
          <p className="font-mono text-sm text-gray-600 mb-6">
            Create an account or sign in to apply for this event.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => navigate(`/login?redirect=/events/${eventId}/apply`)}
              fullWidth
            >
              SIGN IN
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/register?redirect=/events/${eventId}/apply`)}
              fullWidth
            >
              REGISTER
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="lg" label="Loading event..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  // Success state
  if (applicationSuccess) {
    return (
      <div className="min-h-screen bg-white">
        <PageHeader
          title="Application Submitted!"
          breadcrumbs={[
            { label: 'Events', onClick: () => navigate('/guest') },
            { label: event?.title || 'Event' },
          ]}
        />

        <div className="max-w-2xl mx-auto px-6 py-12 text-center">
          {/* Success icon */}
          <div className="w-20 h-20 mx-auto mb-6 border-4 border-black flex items-center justify-center bg-white">
            <svg
              className="w-10 h-10 text-black"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="square"
                strokeLinejoin="miter"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h2 className="font-mono text-2xl font-bold uppercase tracking-wider text-black mb-4">
            YOU'RE IN THE QUEUE!
          </h2>

          <p className="font-mono text-sm text-gray-600 max-w-md mx-auto mb-8">
            {event?.mode === 'curated'
              ? 'Your application has been submitted. The host will review it and you will be notified of their decision.'
              : 'Your application has been accepted! Your P$ are held in escrow until the event is greenlighted.'}
          </p>

          {/* Application summary */}
          <div className="border-2 border-black p-6 text-left mb-8">
            <h3 className="font-mono text-lg font-bold uppercase tracking-wider text-black mb-4">
              {event?.title}
            </h3>
            <div className="space-y-2">
              <p className="font-mono text-sm text-gray-600">
                <span className="font-semibold">DATE:</span> {formatDate(event?.date)}
              </p>
              <p className="font-mono text-sm text-gray-600">
                <span className="font-semibold">LOCATION:</span> {event?.location}
              </p>
              <p className="font-mono text-sm text-gray-600">
                <span className="font-semibold">YOUR PLEDGE:</span>{' '}
                {(submittedApplication?.pledgeAmountCents || event?.ticketPriceCents || 0) === 0 ? (
                  <span className="font-bold text-black">FREE</span>
                ) : (
                  `P$${((submittedApplication?.pledgeAmountCents || event?.ticketPriceCents || 0) / 100).toFixed(2)}`
                )}
              </p>
              <p className="font-mono text-sm text-gray-600">
                <span className="font-semibold">STATUS:</span>{' '}
                <span className="uppercase">
                  {submittedApplication?.status || 'pending'}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button onClick={() => navigate('/guest')} fullWidth>
              GO TO DASHBOARD
            </Button>
            <Button variant="secondary" onClick={() => navigate('/guest')} fullWidth>
              BROWSE MORE EVENTS
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PageHeader
        title="Apply to Event"
        breadcrumbs={[
          { label: 'Events', onClick: () => navigate('/guest') },
          { label: event?.title || 'Event' },
        ]}
      />

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Event Details */}
        <div className="border-2 border-black p-6 mb-8">
          {/* Event poster */}
          {event?.posterUrl && (
            <div className="mb-6 -mx-6 -mt-6">
              <img
                src={event.posterUrl}
                alt={event.title}
                className="w-full h-48 object-cover border-b-2 border-black"
              />
            </div>
          )}

          <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
            {event?.title}
          </h2>

          <div className="space-y-2 mb-4">
            <p className="font-mono text-sm text-gray-600">
              <span className="font-semibold uppercase">DATE:</span>{' '}
              {formatDate(event?.date)}
            </p>
            <p className="font-mono text-sm text-gray-600">
              <span className="font-semibold uppercase">LOCATION:</span>{' '}
              {event?.location}
            </p>
            <p className="font-mono text-sm text-gray-600">
              <span className="font-semibold uppercase">TICKET PRICE:</span>{' '}
              {event?.ticketPriceCents === 0 ? (
                <span className="font-bold text-black">FREE</span>
              ) : (
                `P$${((event?.ticketPriceCents || 0) / 100).toFixed(2)}`
              )}
            </p>
          </div>

          {event?.description && (
            <div className="pt-4 border-t-2 border-black">
              <p className="font-mono text-sm text-gray-700 whitespace-pre-wrap">
                {event.description}
              </p>
            </div>
          )}

          {/* Host info */}
          {event?.host && (
            <div className="mt-4 pt-4 border-t-2 border-black flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-black overflow-hidden bg-gray-100 flex items-center justify-center">
                {event.host.avatarUrl ? (
                  <img
                    src={event.host.avatarUrl}
                    alt={event.host.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-mono text-sm font-bold">
                    {event.host.displayName?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <div>
                <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                  HOSTED BY
                </p>
                <p className="font-mono text-sm font-semibold text-black">
                  {event.host.displayName}
                </p>
              </div>
            </div>
          )}

          {/* Mode indicator */}
          <div className="mt-4 flex items-center gap-2">
            <span className="px-3 py-1 font-mono text-xs font-semibold uppercase tracking-wider border border-black bg-gray-100">
              {event?.mode === 'curated' ? 'CURATED EVENT' : 'OPEN EVENT'}
            </span>
            <span className="font-mono text-xs text-gray-500">
              {event?.mode === 'curated'
                ? 'Host reviews applications'
                : 'Auto-accept applications'}
            </span>
          </div>
        </div>

        {/* Party Supplies */}
        {event?.partySupplies?.length > 0 && (
          <PartySuppliesSection
            supplies={event.partySupplies}
            onClaim={async (supplyId, claim) => {
              const result = await eventService.claimPartySupply(supplyId, claim);
              if (result.success) {
                // Re-fetch event to get updated supplies
                let refreshResult = await eventService.getEventByCode(eventId);
                if (!refreshResult.success) {
                  refreshResult = await eventService.getEvent(eventId);
                }
                if (refreshResult.success) {
                  setEvent(refreshResult.data);
                }
              } else {
                alert(result.error || 'Failed to update claim');
              }
            }}
          />
        )}

        {/* Application Form or Already Applied State */}
        {existingApplication ? (
          <div className="space-y-6">
            <div className="p-6 border-2 border-black bg-gray-50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 border-2 border-black flex items-center justify-center bg-white">
                  <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                    ALREADY APPLIED
                  </h3>
                  <p className="font-mono text-xs text-gray-600">
                    You have an existing application for this event.
                  </p>
                </div>
              </div>

              <div className="border-t-2 border-black pt-4">
                <p className="font-mono text-sm text-gray-600 mb-2">
                  <span className="font-semibold uppercase">STATUS:</span>{' '}
                  <span className="uppercase">{existingApplication.status}</span>
                </p>
                {existingApplication.message && (
                  <p className="font-mono text-sm text-gray-600 mb-2">
                    <span className="font-semibold uppercase">MESSAGE:</span>{' '}
                    {existingApplication.message}
                  </p>
                )}

                {/* Commitment Breakdown */}
                <div className="mt-4 border-t border-gray-300 pt-3">
                  <p className="font-mono text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                    COMMITMENT BREAKDOWN
                  </p>
                  <div className="space-y-1">
                    <div className="flex justify-between font-mono text-sm text-gray-600">
                      <span>Original Pledge</span>
                      <span>P${((existingApplication.pledgeAmountCents || 0) / 100).toFixed(2)}</span>
                    </div>
                    {mimeHistory
                      .filter((r) => r.status === 'Accepted')
                      .map((r, i) => (
                        <div key={r.id} className="flex justify-between font-mono text-sm text-gray-600">
                          <span>MIME Round {i + 1}</span>
                          <span>+ P${((r.committedAmountCents || r.additionalCommitmentCents || 0) / 100).toFixed(2)}</span>
                        </div>
                      ))}
                    <div className="flex justify-between font-mono text-sm font-bold text-black border-t border-gray-300 pt-1 mt-1">
                      <span>TOTAL COMMITTED</span>
                      <span>P${((existingApplication.partyCreditsCommitted || existingApplication.pledgeAmountCents || 0) / 100).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pending MIME Requests */}
            {mimeRequests.length > 0 && (
              <div>
                <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black mb-3">
                  MAKE IT MORE EPIC
                </h3>
                <div className="space-y-4">
                  {mimeRequests.map((req) => (
                    <MimeRequestCard
                      key={req.id}
                      request={req}
                      onAccept={handleMimeAccept}
                      onDecline={handleMimeDecline}
                      isResponding={respondingMimeId === req.id}
                      error={mimeErrors[req.id] || null}
                    />
                  ))}
                </div>
              </div>
            )}

            <Button
              variant="primary"
              fullWidth
              size="lg"
              onClick={() => navigate('/guest')}
            >
              GO TO DASHBOARD
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="font-mono text-sm text-gray-600 uppercase tracking-wider hover:text-black transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        ) : (
          <ApplyForm event={event} onSuccess={handleSuccess} />
        )}
      </div>
    </div>
  );
}
